// Session identity + local persistence for abandon-recovery.
//
// A UUID v4 is minted on first load and kept in localStorage; every /public/intake
// and /public/account call carries it so partial answers accumulate server-side and
// an abandoned funnel is recoverable. We also cache the answer map locally so a
// returning visitor resumes instantly without a round-trip.
//
// NOTE: answers include health info (body part, pain, goal, email). localStorage is
// the patient's own device — acceptable — but this is why the widget is same-origin
// with our API and never posts answers to the host page. See postMessage.ts.

const SESSION_KEY = 'expy.onboarding.sessionId';
const ANSWERS_KEY = 'expy.onboarding.answers';
const STEP_KEY = 'expy.onboarding.step';
const SEEDED_KEY = 'expy.onboarding.seeded';

function uuidv4(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  // Fallback for older embedded webviews.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function safeGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}
function safeSet(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* private mode / disabled storage — degrade to in-memory only */
  }
}

export function getSessionId(): string {
  let id = safeGet(SESSION_KEY);
  if (!id) {
    id = uuidv4();
    safeSet(SESSION_KEY, id);
  }
  return id;
}

export type Answers = Record<string, unknown>;

export function loadAnswers(): Answers {
  const raw = safeGet(ANSWERS_KEY);
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as Answers) : {};
  } catch {
    return {};
  }
}

export function saveAnswers(answers: Answers): void {
  safeSet(ANSWERS_KEY, JSON.stringify(answers));
}

export function loadStep(): number {
  const raw = safeGet(STEP_KEY);
  const n = raw ? Number(raw) : 0;
  return Number.isInteger(n) && n >= 0 ? n : 0;
}

export function saveStep(step: number): void {
  safeSet(STEP_KEY, String(step));
}

// Answer fields seeded by a ?prefill= deep link. The questionnaire skips these on the
// way forward (the visitor already answered them on the page that linked here) but
// still shows them on the way back, so they can be changed.
export function saveSeeded(fields: string[]): void {
  safeSet(SEEDED_KEY, JSON.stringify(fields));
}

export function loadSeeded(): Set<string> {
  try {
    const parsed = JSON.parse(safeGet(SEEDED_KEY) || '[]');
    return new Set(Array.isArray(parsed) ? parsed.filter((f): f is string => typeof f === 'string') : []);
  } catch {
    return new Set();
  }
}

// The account-claim magic link, persisted at account creation so the checkout
// return pages (full page loads — all in-memory state is gone) can still render
// the sign-in QR. Same sensitivity class as the answers: the patient's own
// device, never posted to the host page.
const CLAIM_LINK_KEY = 'expy.onboarding.claimLink';

export function saveClaimLink(link: string): void {
  safeSet(CLAIM_LINK_KEY, link);
}

export function loadClaimLink(): string | undefined {
  return safeGet(CLAIM_LINK_KEY) || undefined;
}

// Called after a successful account conversion — the funnel is done, don't resume it.
export function clearSession(): void {
  [SESSION_KEY, ANSWERS_KEY, STEP_KEY, SEEDED_KEY].forEach((k) => {
    try {
      window.localStorage.removeItem(k);
    } catch {
      /* ignore */
    }
  });
}
